import { useState, useEffect, useCallback } from "react";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export const VoiceSearchButton = ({ onResult, className = "", size = "default" }) => {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [transcript, setTranscript] = useState("");
  const [recognition, setRecognition] = useState(null);

  useEffect(() => {
    // Check for browser support
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      setIsSupported(false);
      return;
    }

    const recognitionInstance = new SpeechRecognition();
    recognitionInstance.continuous = false;
    recognitionInstance.interimResults = true;
    recognitionInstance.lang = "en-US";

    recognitionInstance.onresult = (event) => {
      const current = event.resultIndex;
      const result = event.results[current];
      const text = result[0].transcript;
      
      setTranscript(text);
      
      // If this is a final result, pass it to the callback
      if (result.isFinal) {
        onResult(text);
        setIsListening(false);
        setTranscript("");
      }
    };

    recognitionInstance.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
      setIsListening(false);
      setTranscript("");
    };

    recognitionInstance.onend = () => {
      setIsListening(false);
    };

    setRecognition(recognitionInstance);

    return () => {
      if (recognitionInstance) {
        recognitionInstance.abort();
      }
    };
  }, [onResult]);

  const toggleListening = useCallback(() => {
    if (!recognition) return;

    if (isListening) {
      recognition.stop();
      setIsListening(false);
      setTranscript("");
    } else {
      setTranscript("");
      recognition.start();
      setIsListening(true);
    }
  }, [recognition, isListening]);

  // Don't render if not supported
  if (!isSupported) {
    return null;
  }

  const sizeClasses = {
    small: "w-8 h-8",
    default: "w-10 h-10",
    large: "w-12 h-12",
  };

  const iconSizes = {
    small: "w-4 h-4",
    default: "w-5 h-5",
    large: "w-6 h-6",
  };

  return (
    <div className="relative">
      <motion.button
        type="button"
        onClick={toggleListening}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={`
          ${sizeClasses[size] || sizeClasses.default}
          rounded-full flex items-center justify-center transition-all
          ${isListening 
            ? "bg-red-500 text-white shadow-lg shadow-red-500/50" 
            : "bg-white/10 text-[#94A3B8] hover:bg-[#7C3AED]/20 hover:text-[#7C3AED]"
          }
          ${className}
        `}
        title={isListening ? "Stop listening" : "Voice search"}
        data-testid="voice-search-button"
      >
        <AnimatePresence mode="wait">
          {isListening ? (
            <motion.div
              key="listening"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="relative"
            >
              <MicOff className={iconSizes[size] || iconSizes.default} />
              {/* Pulsing animation ring */}
              <motion.span
                className="absolute inset-0 rounded-full border-2 border-white"
                animate={{ scale: [1, 1.5, 1], opacity: [1, 0, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
            </motion.div>
          ) : (
            <motion.div
              key="idle"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
            >
              <Mic className={iconSizes[size] || iconSizes.default} />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Live transcript tooltip */}
      <AnimatePresence>
        {isListening && transcript && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-2 bg-[#1E293B] border border-white/10 rounded-lg shadow-xl z-50 whitespace-nowrap"
          >
            <div className="flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin text-[#7C3AED]" />
              <span className="text-sm text-white">{transcript}</span>
            </div>
            <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#1E293B] border-l border-t border-white/10 rotate-45" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Listening indicator */}
      <AnimatePresence>
        {isListening && !transcript && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-2 bg-[#1E293B] border border-white/10 rounded-lg shadow-xl z-50 whitespace-nowrap"
          >
            <div className="flex items-center gap-2">
              <motion.div
                className="flex gap-1"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <span className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                <span className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                <span className="w-1.5 h-1.5 bg-red-500 rounded-full" />
              </motion.div>
              <span className="text-sm text-[#94A3B8]">Listening...</span>
            </div>
            <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#1E293B] border-l border-t border-white/10 rotate-45" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VoiceSearchButton;
